import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { brandOr } from '../../utils/brand';
import { moneyTight as money } from '../../utils/region';
import { fmtBkk } from '../../utils/bkkTime';
import { pageToolbar, PAGE_INSET } from '../../tabPalette';   // the page toolbar's bubble + the one page gutter (report 76a2c198)
import Pagination from '../Pagination';
import { DayFirstDateInput, dmyToIso, isoToDmy } from '../DocStorage/UploadWizard';

// ── The Telegram batch report (Calem, Bugify #044e76b0) ──────────────────────────────────────────
// "If the user chooses option 2 more than 5, instead of analyzing the flow immediately, create a
// full-web browser report that pops up on the user's screen, letting them know that it is ready
// (finished) once all bills, invoices, and receipts have been analyzed. Then allow the user to
// preview each uploaded bill (directly on the screen in this report), and load the following
// columns: for check / edit — bill, invoice, tax invoice, receipt or tax invoice receipt (drop down)
// AI has already selected one but user can change; client or supplier full name (text box) AI has
// already selected one but can change; payment date (date field) AI has already selected one but can
// change."
//
// One pile of photos closed with ✅ Done in the accounting bot = one of these pages. The scan sits
// beside the three boxes so the paper and the answer are read together; everything else on the row
// (the date, the number, the amount) is there to recognise the document by and is NOT editable here
// — those are the tax figures, and they are corrected in the Tax module where the filing lock and
// the arithmetic check apply. Nothing this page saves touches a figure (api.taxLineDocFields).
//
// The last column is Calem's tick: "provide a complete tick box for each line for the user to
// confirm that everything is correct". It saves the moment it is tapped, like every other box on the
// row, and the toolbar counts how many of the batch have been ticked. As soon as that save comes
// back the line carries the confirmation in writing beside the box — "Confirmed by <name>" and the
// minute, in the done green: "there should be some instant confirmation of sorts". It blocks
// nothing — a batch with lines left unticked is simply a batch somebody has not finished reading.
//
// The "it is ready" card that leads here is Notice.tsx; opening the page is what takes it down.

type Img = { id: string; mime: string };
interface Row {
  id: string; line_date: string; doc_no: string; party_name: string; section: string;
  amount: number; vat: number; total: number; calc_error: string;
  images: Img[]; docType: string; paymentDate: string;
  /** "Everything on this line is correct", and who said so when. */
  checked: boolean; checkedByName: string; checkedAt: number;
  /** The payment-date box as it is being typed, DD/MM/YYYY. The stored date is `paymentDate`; this is
   *  what the person can see, so half a date stays on screen instead of snapping back. */
  paidTxt: string;
}
interface Head { id: string; at: number; count: number; needs: number; checked: number; who: string; client: string }

// Calem's five, in the order and the wording of the report (the i18n keys carry the words).
const DOC_TYPES = ['bill', 'invoice', 'tax_invoice', 'receipt', 'tax_invoice_receipt'];

const box: React.CSSProperties = {
  width: '100%', padding: '7px 9px', fontSize: 13, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', borderRadius: 8, color: 'var(--text-primary)', boxSizing: 'border-box', outline: 'none',
};

/** The scan itself, on the row. A click opens it full size — the paper is the thing being checked. */
function Scan({ images, onOpen }: { images: Img[]; onOpen: () => void }) {
  const { t } = useTranslation();
  const first = images && images[0];
  const isPdf = !!first && first.mime === 'application/pdf';
  const [src, setSrc] = useState('');
  useEffect(() => {
    let url = ''; let alive = true;
    if (first && !isPdf) api.taxImageBlob(first.id).then((u: string) => { if (alive) { url = u; setSrc(u); } }).catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [first?.id]); // eslint-disable-line
  if (!first) return <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('txb_no_scan')}</span>;
  return (
    <button onClick={onOpen} title={t('txb_preview')}
      style={{ border: '1px solid var(--border)', background: 'var(--c-gray-100)', borderRadius: 8, padding: 0, cursor: 'pointer', position: 'relative', lineHeight: 0, width: 116, height: 88, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {isPdf ? <i className="ti ti-file-type-pdf" style={{ fontSize: 28, color: 'var(--text-secondary)' }} aria-hidden />
        : src ? <img src={src} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <i className="ti ti-photo" style={{ fontSize: 22, color: 'var(--text-tertiary)' }} aria-hidden />}
      {images.length > 1 && <span style={{ position: 'absolute', top: 3, right: 3, fontSize: 9.5, fontWeight: 700, background: 'var(--accent)', color: '#fff', borderRadius: 8, padding: '0 5px' }}>{images.length}</span>}
    </button>
  );
}

const lbBtn: React.CSSProperties = { border: '1px solid #ffffff55', background: '#ffffff22', color: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 14 };

function Lightbox({ images, onClose }: { images: Img[]; onClose: () => void }) {
  const { t } = useTranslation();
  const [i, setI] = useState(0);
  const [src, setSrc] = useState('');
  const cur = images[i];
  const isPdf = !!cur && cur.mime === 'application/pdf';
  useEffect(() => {
    let url = ''; let alive = true;
    if (cur) api.taxImageBlob(cur.id).then((u: string) => { if (alive) { url = u; setSrc(u); } }).catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [cur?.id]); // eslint-disable-line
  useEffect(() => { const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, []); // eslint-disable-line
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        {isPdf ? (src ? <iframe title="pdf" src={src} style={{ width: '82vw', height: '84vh', border: 'none', background: '#fff', borderRadius: 6 }} /> : <div style={{ color: '#fff' }}>{t('txb_loading')}</div>)
          : (src ? <img src={src} alt="" style={{ maxWidth: '92vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 6 }} /> : <div style={{ color: '#fff' }}>{t('txb_loading')}</div>)}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {images.length > 1 && <>
            <button onClick={() => setI(p => (p - 1 + images.length) % images.length)} style={lbBtn}>‹</button>
            <span style={{ color: '#fff', fontSize: 13 }}>{i + 1} / {images.length}</span>
            <button onClick={() => setI(p => (p + 1) % images.length)} style={lbBtn}>›</button>
          </>}
          <button onClick={onClose} style={lbBtn}>✕ {t('txb_close')}</button>
        </div>
      </div>
    </div>
  );
}

export function TaxBatchView({ id }: { id: string }) {
  const { t, i18n } = useTranslation();
  const [head, setHead] = useState<Head | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(100);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(true);
  const [gone, setGone] = useState(false);
  const [err, setErr] = useState('');
  const [state, setState] = useState<Record<string, string>>({});   // line id → 'saving' | 'saved' | an error
  const [open, setOpen] = useState<Img[] | null>(null);

  // What the server last told us each box holds, so a box that was only looked at is never written
  // back: every write stamps the tax line's "edited by / at", and an audit entry for no change is a
  // lie about who touched the invoice.
  const held = useRef<Record<string, { docType: string; paymentDate: string; party: string; checked: boolean }>>({});

  const load = useCallback(async (p: number) => {
    setBusy(true); setErr('');
    try {
      const d: any = await api.taxBatch(id, p);
      // A batch nobody kept is not an error — the server says so rather than refusing (routes/tax.ts).
      if (d?.gone) { setGone(true); setRows([]); setHead(null); return; }
      setGone(false);
      setHead(d.batch || null);
      const got: any[] = Array.isArray(d.rows) ? d.rows : [];
      const rs: Row[] = got.map((r: any) => ({ ...r, checked: !!r.checked, paidTxt: r.paymentDate ? isoToDmy(r.paymentDate) : '' }));
      held.current = {};
      for (const r of rs) held.current[r.id] = { docType: r.docType || '', paymentDate: r.paymentDate || '', party: r.party_name || '', checked: !!r.checked };
      setRows(rs);
      setTotal(Number(d.total) || 0);
      setLimit(Number(d.limit) || 100);
      setPage(Number(d.page) || p);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }, [id]);
  useEffect(() => { void load(1); }, [load]);

  const edit = (lineId: string, patch: Partial<Row>) => setRows(rs => rs.map(r => (r.id === lineId ? { ...r, ...patch } : r)));

  // One save per box, carrying only that box: the short path of PATCH /tax/lines/:id writes the
  // stored answers and the party name and leaves every figure on the line alone.
  const save = async (r: Row, patch: { docType?: string; paymentDate?: string; partyName?: string; checked?: boolean }) => {
    const was = held.current[r.id] || { docType: '', paymentDate: '', party: '', checked: false };
    if (patch.docType !== undefined && patch.docType === was.docType) return;
    if (patch.paymentDate !== undefined && patch.paymentDate === was.paymentDate) return;
    if (patch.partyName !== undefined && patch.partyName === was.party) return;
    if (patch.checked !== undefined && patch.checked === was.checked) return;
    setState(s => ({ ...s, [r.id]: 'saving' }));
    try {
      const d: any = await api.taxLineDocFields(r.id, patch);
      held.current[r.id] = {
        docType: patch.docType === undefined ? was.docType : patch.docType,
        paymentDate: patch.paymentDate === undefined ? was.paymentDate : patch.paymentDate,
        party: patch.partyName === undefined ? was.party : patch.partyName,
        checked: patch.checked === undefined ? was.checked : patch.checked,
      };
      // A tick moves the toolbar's count, and the stamp beside it comes back from the server — who
      // ticked it is the login the server knows, never one this page sent.
      if (patch.checked !== undefined) {
        edit(r.id, { checkedByName: String(d?.checkedByName || ''), checkedAt: Number(d?.checkedAt) || 0 });
        setHead(h => (h ? { ...h, checked: Math.max(0, h.checked + (patch.checked ? 1 : -1)) } : h));
      }
      setState(s => ({ ...s, [r.id]: 'saved' }));
    } catch (e: any) { setState(s => ({ ...s, [r.id]: e.message || t('txb_save_failed') })); }
  };

  // The heading band every staff list in the system wears (reports 4bd4ae4e / 66c9d174 / c151d269).
  const th: React.CSSProperties = { position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-surface)', padding: '10px 10px 6px', fontSize: 11.5, fontWeight: 400, color: 'var(--text-primary)', lineHeight: '16px', textAlign: 'left', whiteSpace: 'nowrap', boxShadow: 'inset 0 -1px 0 var(--border-strong)' };
  const td: React.CSSProperties = { padding: '8px 10px', fontSize: 12.5, borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-app)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', ...pageToolbar(), background: 'var(--bg-surface)', flexWrap: 'wrap' }}>
        <b style={{ fontSize: 15 }}>{t('txb_title')}</b>
        {!!head && <>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{head.client || '—'}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('txb_count', { n: head.count })}</span>
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{fmtBkk(head.at * 1000, i18n.language)}</span>
          {head.needs > 0 && (
            <span style={{ fontSize: 12, fontWeight: 700, color: '#8A6212', background: '#FEF6E0', border: '1px solid #F0DCA6', borderRadius: 999, padding: '2px 9px' }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 12, marginRight: 5, verticalAlign: '-2px' }} aria-hidden />{t('txb_needs', { n: head.needs })}
            </span>
          )}
          {/* How far through the batch the person is — all of it ticked reads in the done colour. */}
          <span style={{ fontSize: 12.5, fontWeight: 700, color: head.count > 0 && head.checked >= head.count ? '#0F6B49' : 'var(--text-secondary)' }}>
            {t('txb_checked_n', { n: head.checked, m: head.count })}
          </span>
        </>}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('txb_hint')}</span>
      </div>

      {busy && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 24, display: 'inline-block', animation: 'spin 1s linear infinite' }} /></div>}
      {!!err && <div style={{ padding: 20, color: '#B42318' }}>{err}</div>}
      {!busy && gone && <div style={{ padding: 44, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13.5 }}>{t('txb_gone')}</div>}

      {!busy && !err && !gone && (
        <div style={{ flex: 1, overflow: 'auto', padding: `0 ${PAGE_INSET}px` }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>
              <th style={{ ...th, width: 130 }}>{t('txb_col_scan')}</th>
              <th style={{ ...th, width: 96 }}>{t('txb_col_date')}</th>
              <th style={{ ...th, width: 150 }}>{t('txb_col_docno')}</th>
              <th style={{ ...th, width: 120, textAlign: 'right' }}>{t('txb_col_total')}</th>
              <th style={{ ...th, width: 190 }}>{t('txb_col_type')}</th>
              <th style={th}>{t('txb_col_party')}</th>
              <th style={{ ...th, width: 150 }}>{t('txb_col_paid')}</th>
              <th style={{ ...th, width: 168 }}>{t('txb_col_check')}</th>
              <th style={{ ...th, width: 34 }} aria-label={t('txb_col_saved')} />
            </tr></thead>
            <tbody>
              {rows.map(r => {
                const st = state[r.id] || '';
                return (
                  <tr key={r.id}>
                    <td style={td}><Scan images={r.images || []} onOpen={() => setOpen(r.images || [])} /></td>
                    <td style={{ ...td, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{r.line_date ? isoToDmy(r.line_date) : '—'}</td>
                    <td style={{ ...td, color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{r.doc_no || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {money(r.total)}
                      {!!r.calc_error && <i className="ti ti-alert-triangle" title={t('txb_calc_error')} style={{ fontSize: 12, color: '#B42318', marginLeft: 5, verticalAlign: '-2px' }} aria-hidden />}
                    </td>
                    <td style={td}>
                      <select value={r.docType || ''} onChange={e => { edit(r.id, { docType: e.target.value }); void save(r, { docType: e.target.value }); }}
                        style={{ ...box, cursor: 'pointer' }}>
                        <option value="">{t('txb_type_unset')}</option>
                        {DOC_TYPES.map(k => <option key={k} value={k}>{t(`txb_type_${k}`)}</option>)}
                      </select>
                    </td>
                    <td style={td}>
                      <input value={r.party_name || ''} placeholder={t('txb_party_ph')}
                        onChange={e => edit(r.id, { party_name: e.target.value })}
                        onBlur={e => { void save(r, { partyName: e.target.value }); }}
                        style={box} />
                    </td>
                    <td style={td}>
                      {/* Day-first, as every date box in the system is (report b7c020ac); it is stored as
                          a plain calendar date. Saved on the keystroke that completes it, and on clearing —
                          a half-typed date stays in the box and nothing is written until it is a real date. */}
                      <DayFirstDateInput value={r.paidTxt || ''} placeholder={t('txb_paid_ph')} style={box}
                        onChange={v => {
                          const iso = dmyToIso(v);
                          edit(r.id, { paidTxt: v, ...(iso || !v.trim() ? { paymentDate: iso } : {}) });
                          if (iso || !v.trim()) void save(r, { paymentDate: iso });
                        }} />
                    </td>
                    <td style={{ ...td, color: r.checked && r.checkedAt ? '#0F6B49' : undefined }}>
                      {/* Calem's tick: the person confirming that this whole line is correct. Saved on
                          the tap, stamped with their name and the minute by the server — and the moment
                          that save comes back the line SAYS so, in the done green, beside the box: a tick
                          on its own left the confirmation hidden in the box's hover. The name is always
                          the one the server sent back, never one this page knows. Unticking takes the
                          stamp down with it (saveDocFields clears it). */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={!!r.checked}
                          title={r.checked && r.checkedAt ? t('txb_checked_by', { name: r.checkedByName || '—', when: fmtBkk(r.checkedAt * 1000, i18n.language) }) : t('txb_check_hint')}
                          onChange={e => { edit(r.id, { checked: e.target.checked }); void save(r, { checked: e.target.checked }); }}
                          style={{ cursor: 'pointer', width: 15, height: 15, accentColor: '#0F6B49', flex: 'none' }} />
                        {r.checked && !!r.checkedAt && (
                          <div style={{ minWidth: 0, lineHeight: '14px' }}>
                            <div style={{ fontSize: 11.5, fontWeight: 700 }}>
                              <i className="ti ti-circle-check" style={{ fontSize: 13, marginRight: 4, verticalAlign: '-2px' }} aria-hidden />
                              {t('txb_confirmed_by', { name: r.checkedByName || '—' })}
                            </div>
                            <div style={{ fontSize: 11 }}>{fmtBkk(r.checkedAt * 1000, i18n.language)}</div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {st === 'saving' && <i className="ti ti-loader" style={{ fontSize: 13, color: 'var(--text-tertiary)', display: 'inline-block', animation: 'spin 1s linear infinite' }} aria-hidden />}
                      {st === 'saved' && <i className="ti ti-check" title={t('txb_saved')} style={{ fontSize: 14, color: '#0F6B49' }} aria-hidden />}
                      {!!st && st !== 'saving' && st !== 'saved' && <i className="ti ti-alert-circle" title={st} style={{ fontSize: 14, color: '#B42318' }} aria-hidden />}
                    </td>
                  </tr>
                );
              })}
              {!rows.length && <tr><td colSpan={9} style={{ ...td, textAlign: 'center', padding: 44, color: 'var(--text-tertiary)' }}>{t('txb_empty')}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {!busy && !err && !gone && <Pagination page={page} total={total} limit={limit} onPage={p => void load(p)} />}
      {!!open && <Lightbox images={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

export default function TaxBatch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('mod_tax_batch')}</div>
      </header>
      <TaxBatchView id={id} />
    </div>
  );
}
